import React, {useCallback, useEffect, useMemo, useState} from "react";
import {isEmpty} from "lodash";
import {Switch} from "antd";
import {useTypedSelector} from "migration/shared/lib/hooks/useTypedSelector";
import {Notify} from "migration/shared/lib/notification/notification";
import {useGlobalContext, useUserContext} from "../../../../../../../hooks/globalContext";
import {FormWithLoader} from "../../../../../../../components/loader/formWithLoader";
import IikoService, {IikoModifiersVisibilitySettings} from "../../api/iikoService";
import {IikoExtMenuModifierStopList} from "./IikoExtMenuModifierStopList";

interface ModifierGroup {
    id: string;
    name: string;
    children: { id: string; name: string }[];
    isUngrouped?: boolean;
}

export function IikoHideModifiers() {
    const {i18n} = useGlobalContext();
    const {currentMenuId, user} = useUserContext();
    const {
        iikoCompanyId,
        iikoMenuSettings,
    } = useTypedSelector(state => state.iiko);

    const [isLoading, setIsLoading] = useState(false);
    const [visibilityData, setVisibilityData] = useState<IikoModifiersVisibilitySettings | null>(null);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(new Set());
    const [hiddenChildren, setHiddenChildren] = useState<Set<string>>(new Set());
    const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());

    const fetchVisibilitySettings = useCallback(async (controller: AbortController) => {
        if (!iikoCompanyId || !iikoMenuSettings?.iikoOrganizationId) return;

        setIsLoading(true);
        try {
            const res = await IikoService.getIikoModifiersVisibilitySettings({
                organizationId: user?.org?.id || "",
                menuId: currentMenuId,
                iikoOrganizationId: iikoMenuSettings.iikoOrganizationId,
                iikoCompanyId: iikoCompanyId,
            }, controller);

            if (res.data.success) {
                const data = res.data.data as IikoModifiersVisibilitySettings;
                setVisibilityData(data);
                setHiddenGroups(new Set(data.visibilitySettings?.hiddenGroupModifiers || []));
                setHiddenChildren(new Set(data.visibilitySettings?.hiddenChildModifiers || []));
            } else {
                Notify.Warning({
                    title: (res.data as any)?.error?.message || "Error",
                    message: "",
                });
            }
        } catch (e: any) {
            if (e?.name !== "CanceledError") {
                Notify.Warning({title: "Error", message: ""});
            }
        } finally {
            setIsLoading(false);
        }
    }, [iikoCompanyId, iikoMenuSettings?.iikoOrganizationId, user?.org?.id, currentMenuId]);

    useEffect(() => {
        const controller = new AbortController();
        fetchVisibilitySettings(controller);
        return () => controller.abort();
    }, [fetchVisibilitySettings]);

    const modifierGroups: ModifierGroup[] = useMemo(() => {
        if (!visibilityData) return [];

        const ungroupedChildren: { id: string; name: string }[] = [];
        const childrenByGroup = new Map<string, { id: string; name: string }[]>();
        (visibilityData.childModifiers || []).forEach(cm => {
            if (!cm.groupId) {
                ungroupedChildren.push({id: cm.id, name: cm.name});
                return;
            }
            const list = childrenByGroup.get(cm.groupId) || [];
            list.push({id: cm.id, name: cm.name});
            childrenByGroup.set(cm.groupId, list);
        });

        const groups: ModifierGroup[] = (visibilityData.groupModifiers || []).map(gm => ({
            id: gm.id,
            name: gm.name,
            children: childrenByGroup.get(gm.id) || [],
        }));

        if (ungroupedChildren.length > 0) {
            groups.push({
                id: "__ungrouped__",
                name: i18n.ungrouped_modifiers(),
                children: ungroupedChildren,
                isUngrouped: true,
            });
        }

        return groups;
    }, [visibilityData, i18n]);

    const toggleExpand = useCallback((groupId: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    }, []);

    const addLoadingId = (id: string) => {
        setLoadingIds(prev => new Set(prev).add(id));
    };

    const removeLoadingId = (id: string) => {
        setLoadingIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const handleToggleGroup = async (groupId: string, visible: boolean) => {
        addLoadingId(groupId);
        try {
            const request = {
                organizationId: user?.org?.id || "",
                menuId: currentMenuId,
                iikoGroupId: groupId,
                iikoCompanyId: iikoCompanyId,
                iikoOrganizationId: iikoMenuSettings?.iikoOrganizationId || "",
            };

            const res = visible
                ? await IikoService.cmdRevealGroupModifier(request)
                : await IikoService.cmdHideGroupModifier(request);

            if (res.data.success) {
                if (visible) {
                    setHiddenGroups(prev => {
                        const next = new Set(prev);
                        next.delete(groupId);
                        return next;
                    });
                } else {
                    setHiddenGroups(prev => new Set(prev).add(groupId));
                }
            } else {
                Notify.Warning({
                    title: (res.data as any)?.error?.message || "Error",
                    message: "",
                });
            }
        } catch {
            Notify.Warning({title: "Error", message: ""});
        } finally {
            removeLoadingId(groupId);
        }
    };

    const handleToggleChild = async (childId: string, visible: boolean) => {
        addLoadingId(childId);
        try {
            const request = {
                organizationId: user?.org?.id || "",
                menuId: currentMenuId,
                iikoModifierId: childId,
                iikoCompanyId: iikoCompanyId,
                iikoOrganizationId: iikoMenuSettings?.iikoOrganizationId || "",
            };

            const res = visible
                ? await IikoService.cmdRevealChildModifier(request)
                : await IikoService.cmdHideChildModifier(request);

            if (res.data.success) {
                if (visible) {
                    setHiddenChildren(prev => {
                        const next = new Set(prev);
                        next.delete(childId);
                        return next;
                    });
                } else {
                    setHiddenChildren(prev => new Set(prev).add(childId));
                }
            } else {
                Notify.Warning({
                    title: (res.data as any)?.error?.message || "Error",
                    message: "",
                });
            }
        } catch {
            Notify.Warning({title: "Error", message: ""});
        } finally {
            removeLoadingId(childId);
        }
    };

    if (!iikoCompanyId) {
        return (
            <div className="alert alert-warning py-4">
                {i18n.help_empty_iiko()}
            </div>
        );
    }

    if (isEmpty(iikoMenuSettings)) {
        return (
            <div className="alert alert-warning py-4">
                {i18n.select_organization()}
            </div>
        );
    }

    if (iikoMenuSettings?.iikoExternalMenuId) {
        return <IikoExtMenuModifierStopList />;
    }

    return (
        <FormWithLoader
            isloading={isLoading}
            id="hideModifiers"
            className={"form"}
            style={{padding: "0"}}
        >
            <h3>{i18n.modifier_visibility_settings()}</h3>

            {modifierGroups.length === 0 && !isLoading && (
                <div className="alert alert-info py-4">
                    {i18n.no_group_modifiers_found()}
                </div>
            )}

            <div style={{display: "flex", flexDirection: "column", gap: "8px"}}>
                {modifierGroups.map(group => {
                    const isGroupHidden = !group.isUngrouped && hiddenGroups.has(group.id);
                    const isExpanded = group.isUngrouped || expandedGroups.has(group.id);

                    return (
                        <div
                            key={group.id}
                            style={{
                                border: "1px solid #e8e8e8",
                                borderRadius: "8px",
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "12px 16px",
                                    backgroundColor: "#fafafa",
                                }}
                            >
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        flex: 1,
                                        cursor: group.isUngrouped ? "default" : "pointer",
                                    }}
                                    onClick={() => !group.isUngrouped && toggleExpand(group.id)}
                                >
                                    {!group.isUngrouped && (
                                        <span
                                            style={{
                                                fontSize: "12px",
                                                transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                                                transition: "transform 0.2s",
                                            }}
                                        >
                                            ▶
                                        </span>
                                    )}
                                    <span style={{fontWeight: 500}}>{group.name}</span>
                                    <span style={{color: "#999", fontSize: "12px"}}>
                                        ({group.children.length})
                                    </span>
                                </div>

                                {!group.isUngrouped && (
                                    <Switch
                                        checked={!isGroupHidden}
                                        loading={loadingIds.has(group.id)}
                                        onChange={(checked) => handleToggleGroup(group.id, checked)}
                                    />
                                )}
                            </div>

                            {isExpanded && group.children.length > 0 && (
                                <div style={{padding: "0 16px 12px 40px"}}>
                                    {group.children.map((child, index) => {
                                        const isChildHidden = hiddenChildren.has(child.id);
                                        const isLast = index === group.children.length - 1;

                                        return (
                                            <div
                                                key={child.id}
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    padding: "8px 0",
                                                    borderBottom: isLast ? "none" : "1px solid #f0f0f0",
                                                    opacity: isGroupHidden ? 0.5 : 1,
                                                }}
                                            >
                                                <span style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                }}>
                                                    <span style={{color: "#ccc", fontFamily: "monospace"}}>
                                                        {isLast ? "└──" : "├──"}
                                                    </span>
                                                    {child.name}
                                                </span>

                                                <Switch
                                                    checked={!isChildHidden}
                                                    loading={loadingIds.has(child.id)}
                                                    disabled={isGroupHidden}
                                                    onChange={(checked) => handleToggleChild(child.id, checked)}
                                                    size="small"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </FormWithLoader>
    );
}
